import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import configData from '../../configure';
import './Faculty.css';
import { useAuth } from '../../AuthContext';
import { toast } from 'react-toastify';

function FacultyBulkImport({ orgId: propOrgId }) {
  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [importedFaculty, setImportedFaculty] = useState([]);
  const [importSummary, setImportSummary] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { axios, token, orgId: contextOrgId } = useAuth();
  const orgId = propOrgId || contextOrgId || location.state?.orgId;

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (
        selectedFile.type !== 'text/csv' &&
        !selectedFile.name.toLowerCase().endsWith('.csv')
      ) {
        toast.error('Please upload a valid CSV file.');
        setUploadStatus('error');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setUploadStatus('idle');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.warn('No file selected.');
      setUploadStatus('error');
      return;
    }
    if (!orgId) {
      toast.error('Organization ID is missing.');
      setUploadStatus('error');
      return;
    }

    setUploadStatus('uploading');
    setIsProcessing(true);
    setImportSummary(null);

    const fileReader = new FileReader();
    fileReader.onload = async (e) => {
      const csvText = e.target.result;
      const rows = csvText.split('\n').slice(1); // Skip header row

      const facultyToProcess = [];
      const stats = {
        total: 0,
        success: 0,
        duplicates: 0,
        failed: 0,
        errors: []
      };

      // Fetch existing faculty to build translation map for supervisorId
      const facultyMap = new Map();
      try {
        const response = await axios.get(`${configData.GET_USERS_BY_TYPE}/${orgId}`, {
          params: { userType: 'FACULTY', page: 1, pageSize: 2000 },
          headers: { accept: 'application/hal+json' }
        });
        const existingFaculty = response.data?.items || response.data?.content || [];
        existingFaculty.forEach(f => {
          const userId = f.user?.userId || f.userId;
          const email = f.user?.email || f.email;
          const employeeId = f.facultyInfo?.employeeId || f.employeeId;
          const phoneNumber = f.user?.phoneNumber || f.phoneNumber;

          if (userId) facultyMap.set(userId.toLowerCase().trim(), userId);
          if (email) facultyMap.set(email.toLowerCase().trim(), userId);
          if (employeeId) facultyMap.set(employeeId.toLowerCase().trim(), userId);
          if (phoneNumber) {
            const rawPhone = phoneNumber.toLowerCase().trim();
            facultyMap.set(rawPhone, userId);
            const normPhone = String(phoneNumber || '').replace(/\D/g, '').slice(-10);
            if (normPhone) facultyMap.set(normPhone, userId);
          }
        });
      } catch (err) {
        console.error("Failed to fetch existing faculty for mapping supervisorIds", err);
      }

      rows.forEach((row, index) => {
        if (row.trim() === '') return; // Skip empty rows

        const values = row.split(',').map((v) => v.trim());
        if (values.length < 23) {
          stats.failed++;
          stats.errors.push({
            row: index + 2,
            email: 'N/A',
            reason: `Insufficient columns (${values.length} found, 23 required).`
          });
          return;
        }

        const [
          firstName,
          lastName,
          emailRaw,
          phoneNumber,
          gender,
          dob,
          placeOfBirth,
          bloodGroup,
          nationality,
          religion,
          category,
          currentAddress,
          currentAddress2,
          city,
          state,
          pincode,
          alternatePhoneNumber,
          employeeId,
          designation,
          department,
          joiningDate,
          facultyType,
          lastFourDigitsOfAadhaarNumber,
          supervisorIdRaw,
        ] = values;

        let email = emailRaw ? String(emailRaw).trim() : '';
        if (!email && firstName && lastName) {
          const fn = String(firstName).toLowerCase().replace(/[^a-z0-9]/g, '');
          const ln = String(lastName).toLowerCase().replace(/[^a-z0-9]/g, '');
          email = `${fn}.${ln}@sevabharathi.com`;
        }

        const supervisorId = supervisorIdRaw ? String(supervisorIdRaw).trim() : '';

        // Validate required fields
        if (!firstName || !lastName || !email || !employeeId) {
          stats.failed++;
          stats.errors.push({
            row: index + 2,
            email: email || 'Unknown',
            reason: `Missing required fields (firstName, lastName, email, or employeeId).`
          });
          return;
        }

        // Validate email format
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          stats.failed++;
          stats.errors.push({
            row: index + 2,
            email: email,
            reason: `Invalid email format.`
          });
          return;
        }

        facultyToProcess.push({
          originalRow: index + 2,
          email,
          supervisorId,
          payload: {
            user: {
              creationTime: new Date().toISOString(),
              createdBy: 'admin',
              lastUpdatedTime: new Date().toISOString(),
              lastUpdatedBy: 'admin',
              orgId,
              userType: 'FACULTY',
              roleId: 'ROLE_FACULTY',
              userStatus: 'ACTIVE',
              email,
              phoneNumber: phoneNumber || null,
              lastFourDigitsOfAadhaarNumber: lastFourDigitsOfAadhaarNumber || null,
              firstName,
              lastName,
              dateOfBirth: dob ? new Date(dob).toISOString() : null,
              gender: gender ? gender.toUpperCase() : null,
              placeOfBirth: placeOfBirth || null,
              bloodGroup: bloodGroup || null,
              nationality: nationality || 'Indian',
              religion: religion || null,
              category: category || null,
              addressDetails: [
                {
                  addressType: 'CURRENT',
                  addressLine1: currentAddress || 'Unknown',
                  addressLine2: currentAddress2 || null,
                  city: city || 'Unknown',
                  state: state || 'Unknown',
                  country: 'India',
                  pinCode: pincode || '000000',
                  alternatePhoneNumber: alternatePhoneNumber || null,
                },
              ],
            },
            facultyInfo: {
              creationTime: new Date().toISOString(),
              createdBy: 'admin',
              lastUpdatedTime: new Date().toISOString(),
              lastUpdatedBy: 'admin',
              employeeId,
              designation: designation || 'Professor',
              department: department || 'General',
              joiningDate: joiningDate ? new Date(joiningDate).toISOString() : new Date().toISOString(),
              facultyType: facultyType ? facultyType.toUpperCase() : 'LECTURER',
              supervisorId: null
            },
            studentInfo: null,
            parentInfo: null,
            staffInfo: null,
          }
        });
      });

      stats.total = facultyToProcess.length + stats.failed; // Total attempted

      if (facultyToProcess.length === 0 && stats.failed === 0) {
        toast.info('No valid faculty data found in CSV.');
        setUploadStatus('error');
        setIsProcessing(false);
        return;
      }

      try {
        const registeredFaculty = [];

        for (const item of facultyToProcess) {
          const { payload, email, originalRow, supervisorId } = item;

          // Resolve supervisorId to UUID using the live/updated facultyMap
          let resolvedSupervisorId = null;
          if (supervisorId) {
            const cleanSup = supervisorId.toLowerCase().trim();
            const normSup = String(supervisorId || '').replace(/\D/g, '').slice(-10);
            resolvedSupervisorId = facultyMap.get(cleanSup) || (normSup ? facultyMap.get(normSup) : null) || supervisorId;
          }
          payload.facultyInfo.supervisorId = resolvedSupervisorId || null;

          // Backend Creation
          try {
            const response = await axios.post(
              `${configData.REGISTER_USER}`,
              payload,
              {
                headers: {
                  accept: 'application/hal+json',
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            if (response.status === 200 || response.status === 201) {
              stats.success++;
              registeredFaculty.push(response.data);

              // Update facultyMap with the newly created faculty details
              const newUserId = response.data?.userId || response.data?.user?.userId;
              if (newUserId) {
                const createdUser = response.data?.user || response.data;
                const uId = createdUser.userId || newUserId;
                const uEmail = createdUser.email || email;
                const uPhone = createdUser.phoneNumber || payload.user?.phoneNumber;
                const uEmpId = payload.facultyInfo?.employeeId;

                if (uId) facultyMap.set(uId.toLowerCase().trim(), uId);
                if (uEmail) facultyMap.set(uEmail.toLowerCase().trim(), uId);
                if (uPhone) {
                  const rawPhone = uPhone.toLowerCase().trim();
                  facultyMap.set(rawPhone, uId);
                  const normPhone = String(uPhone || '').replace(/\D/g, '').slice(-10);
                  if (normPhone) facultyMap.set(normPhone, uId);
                }
                if (uEmpId) facultyMap.set(uEmpId.toLowerCase().trim(), uId);
              }

              // Send Email Notification
              try {
                const newUserId = response.data?.userId || response.data?.user?.userId;
                if (newUserId && token) {
                  const emailData = {
                    subject: "Welcome to Egurukul - Your Account Details",
                    messageBody: `Welcome to Egurukul!\n\nYour account has been successfully created.\n\nLogin Details:\nUsername: ${email}\nPassword: egurukul@2026\n\nPlease login and change your password immediately.`,
                    userType: "FACULTY",
                    specificUserIds: [newUserId],
                  };

                  const emailFormData = new FormData();
                  emailFormData.append('request', new Blob([JSON.stringify(emailData)], { type: 'application/json' }));

                  await axios.post(configData.SEND_EMAIL_NOTIFICATION, emailFormData, {
                    headers: {
                      Authorization: `Bearer ${token}`,
                      'Content-Type': 'multipart/form-data',
                    },
                  });
                }
              } catch (emailError) {
                console.error(`Failed to send welcome email to ${email}:`, emailError);
              }
            } else {
              throw new Error(`Unexpected status code ${response.status}`);
            }
          } catch (err) {
            const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message;
            console.error(`API Error for ${email}:`, err);
            stats.failed++;
            stats.errors.push({
              row: originalRow,
              email: email,
              reason: errorMsg
            });
          }
        }

        setImportedFaculty(registeredFaculty);
        setImportSummary(stats);
        setUploadStatus('complete');
        setIsProcessing(false);

        if (stats.success > 0) {
          toast.success(`Successfully imported ${stats.success} faculty member(s).`);
        }
        if (stats.failed > 0) {
          toast.error(`Failed to import ${stats.failed} faculty member(s). Check errors below.`);
        }

      } catch (error) { // General error catch block
        console.error('Bulk import error:', error);
        toast.error(`Failed to import faculty: ${error.message}`);
        setUploadStatus('error');
        setIsProcessing(false);
      }
    };

    fileReader.onerror = () => {
      toast.error('Error reading file.');
      setUploadStatus('error');
      setIsProcessing(false);
    };

    fileReader.readAsText(file);
  };




  const handleViewFaculty = () => {
    navigate('/faculty', { state: { orgId } });
  };

  const handleBack = () => {
    navigate('/faculty');
  };

  const generateCSVTemplate = () => {
    const timestamp = Date.now();
    const headers = [
      'firstName',
      'lastName',
      'email',
      'phoneNumber',
      'gender',
      'dob',
      'placeOfBirth',
      'bloodGroup',
      'nationality',
      'religion',
      'category',
      'currentAddress',
      'currentAddress2',
      'city',
      'state',
      'pincode',
      'alternatePhoneNumber',
      'employeeId',
      'designation',
      'department',
      'joiningDate',
      'facultyType',
      'lastFourDigitsOfAadhaarNumber',
      'supervisorId',
    ].join(',');

    const sampleRow1 = [
      'Anita',
      'Sharma',
      `faculty5@gmail.com`,
      '9876543201',
      'FEMALE',
      '1975-03-10',
      'Delhi',
      'A_POSITIVE',
      'Indian',
      'Hindu',
      'General',
      '123 Street',
      '',
      'Delhi',
      'Delhi',
      '110001',
      '9876543213',
      `FAC001`,
      'Professor',
      'Computer Science',
      '2010-06-15',
      'LECTURER',
      '1234',
      '9876543202',
    ].join(',');

    const sampleRow2 = [
      'Vikram',
      'Singh',
      `faculty5@gmail.com`,
      '9876543202',
      'MALE',
      '1980-05-15',
      'Mumbai',
      'B_POSITIVE',
      'Indian',
      'Sikh',
      'General',
      '456 Street',
      'Apt 12',
      'Mumbai',
      'Maharashtra',
      '400001',
      '9876543214',
      `FAC002`,
      'Associate Professor',
      'Mathematics',
      '2012-08-20',
      'LECTURER',
      '5678',
      '9876543201',
    ].join(',');

    return `${headers}\n${sampleRow1}\n${sampleRow2}`;
  };

  return (
    <div className="import-container">
      <div className="import-card">
        <div className="header-section">
          <button className="back-button" onClick={handleBack}>
            ← Back
          </button>
          <h2 className="title">Bulk Faculty Import</h2>
        </div>

        <div className="content-section">
          <div className="upload-area">
            <label className="file-input-label">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden-input"
              />
              <span className="upload-button">📁 Choose CSV File</span>
            </label>
            {file && <p className="file-name">Selected: {file.name}</p>}
          </div>



          <button
            className={`import-button ${uploadStatus === 'uploading' ? 'loading' : ''}`}
            onClick={handleUpload}
            disabled={!file || uploadStatus === 'uploading'}
          >
            {uploadStatus === 'uploading' ? (
              <span className="spinner"></span>
            ) : (
              'Start Import'
            )}
          </button>

          {uploadStatus === 'complete' && importSummary && (
            <div className="summary-section">
              <div className="summary-card">
                <h3>Import Summary</h3>
                <div className="summary-stats">
                  <div className="stat-item warning">
                    <span className="count">{importSummary.duplicates}</span>
                    <span className="label">Duplicates (Skipped)</span>
                  </div>
                  <div className="stat-item error">
                    <span className="count">{importSummary.failed}</span>
                    <span className="label">Failed</span>
                  </div>
                </div>

                {importSummary.errors.length > 0 && (
                  <div className="error-details">
                    <h4>Error Details:</h4>
                    <ul>
                      {importSummary.errors.map((err, idx) => (
                        <li key={idx}>
                          <strong>Row {err.row} ({err.email}):</strong> {err.reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <button
                  className="view-button"
                  onClick={handleViewFaculty}
                  style={{ marginTop: '20px' }}
                >
                  Done & View List
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="csv-instructions">
          <h3>CSV Format Requirements</h3>
          <p>
            Download{' '}
            <a
              href={`data:text/csv;charset=utf-8,${encodeURIComponent(generateCSVTemplate())}`}
              download="FacultyTemplate.csv"
            >
              sample CSV template
            </a>
            . Required fields: firstName, lastName, email, employeeId. Use unique email addresses and employee IDs.
          </p>
        </div>
      </div>
    </div>
  );
}

export default FacultyBulkImport;